// src/pages/relapse/RelapseTrackerPage.tsx - Relapse tracking main page

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Button,
  Fab,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Label as LabelIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { 
  fetchRelapseIncidents, 
  deleteRelapseIncident,
  fetchTriggerTags
} from '../../store/slices/relapseSlice';
import { AppDispatch, RootState } from '../../store';
import { useAuth } from '../../contexts/AuthContext';

const RelapseTrackerPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  
  const { incidents, triggers, isLoading, error } = useSelector((state: RootState) => state.relapse);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [incidentToDelete, setIncidentToDelete] = useState<string | null>(null);
  
  useEffect(() => {
    if (currentUser) {
      dispatch(fetchRelapseIncidents(currentUser.uid));
      dispatch(fetchTriggerTags(currentUser.uid));
    }
  }, [currentUser, dispatch]);
  
  const handleAddIncident = () => {
    navigate('/relapse/add');
  };
  
  const handleEditIncident = (incidentId: string) => {
    navigate(`/relapse/add/${incidentId}`);
  };
  
  const handleDeleteIncident = async (incidentId: string) => {
    if (currentUser) {
      await dispatch(deleteRelapseIncident({
        userId: currentUser.uid,
        incidentId
      }));
    }
  };
  
  const handleManageTriggers = () => {
    navigate('/relapse/triggers');
  };
  
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  const getTriggerColor = (triggerId: string) => {
    const trigger = triggers.find(t => t.id === triggerId);
    return trigger?.color || '#3F51B5';
  };
  
  const getTriggerName = (triggerId: string) => {
    const trigger = triggers.find(t => t.id === triggerId);
    return trigger?.name || 'Unknown';
  };
  
  if (isLoading && incidents.length === 0 && triggers.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Relapse Tracker
        </Typography>
        
        <Button 
          variant="outlined" 
          startIcon={<LabelIcon />}
          onClick={handleManageTriggers}
        >
          Manage Triggers
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Track Your Regressions
            </Typography>
            <Typography variant="body1" paragraph>
              This tool helps you identify patterns in moments of weakness. By tracking when and why you relapse into unwanted behaviors or thought patterns, you can build awareness and develop strategies to maintain discipline.
            </Typography>
            
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleAddIncident}
              sx={{ mt: 1 }}
            >
              Record Incident
            </Button>
          </Paper>
        </Box>
        
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ width: '100%' }}>
            {incidents.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  No Incidents Recorded
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Start tracking your relapses to identify patterns and triggers.
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={handleAddIncident}
                >
                  Record First Incident
                </Button>
              </Box>
            ) : (
              <List>
                {incidents.map((incident, index) => (
                  <React.Fragment key={incident.id}>
                    <ListItem
                      sx={{ py: 2 }}
                      secondaryAction={
                        <Box>
                          <IconButton 
                            edge="end" 
                            aria-label="edit"
                            onClick={() => handleEditIncident(incident.id!)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={() => handleDeleteIncident(incident.id!)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemIcon>
                        <WarningIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mr: 2 }}>
                              {incident.type}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(incident.timestamp)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              {incident.description}
                            </Typography>
                            
                            {incident.triggers && incident.triggers.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {incident.triggers.map((triggerId) => (
                                  <Chip 
                                    key={triggerId}
                                    label={getTriggerName(triggerId)}
                                    size="small"
                                    sx={{ 
                                      backgroundColor: getTriggerColor(triggerId),
                                      color: 'white'
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < incidents.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      </Box>
      
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAddIncident}
      >
        <AddIcon />
      </Fab>
    </Container>
  );
};

export default RelapseTrackerPage;
